import { useMemo } from 'react';

import {
  getAdminApiUrl,
  fetchWithTokenAndRegion,
} from '@adsk/acs-sc-web-platform-api';
import { useSWRAlloy } from '@adsk/alloy-helpers';
import { EntitlementsProject, useAlloyFeatureFlag } from '@adsk/alloy-provider';

import { SideFiltersType } from './useFilters';
import { createEntitlementsFilterParams } from '../clients/admin';
import { DEFAULT_LIMIT, FEATURE_FLAGS, SORT_DIRECTION } from '../constants';

type UseProjectsQueryProps = {
  search?: string;
  sorting: {
    sortKey: keyof EntitlementsProject;
    sortDirection: SORT_DIRECTION;
  };
  pagination: {
    pageIndex: number;
  };
  filters: SideFiltersType;
  classification?: string[];
};

const DATA_EMPTY_STATE = {
  results: null,
  pagination: { totalResults: 0 },
};

export const useProjectsQuery = ({
  search,
  sorting: { sortKey, sortDirection },
  pagination: { pageIndex },
  filters: { project_type, created_on, account },
  classification,
}: UseProjectsQueryProps) => {
  const { value: enableSearchByNameOrNumber } = useAlloyFeatureFlag(
    FEATURE_FLAGS.EnableSearchByNameOrNumber,
  );
  const { value: slashClients } = useAlloyFeatureFlag(
    FEATURE_FLAGS.SlashClient,
  );

  const path = slashClients ? 'ui/v1/clients' : `v2/project-entitlements`;

  const projectsUrl = useMemo(
    () =>
      `${getAdminApiUrl()}/${path}?${createEntitlementsFilterParams({
        name: search,
        jobNumber: enableSearchByNameOrNumber ? search : undefined,
        classification,
        sortKey,
        sortDirection,
        offset: pageIndex * DEFAULT_LIMIT,
        accountIds: account,
        projectType: project_type,
        createdAtRange: created_on,
        orFilters: enableSearchByNameOrNumber
          ? ['name', 'jobNumber'].toString()
          : undefined,
      })}`,
    [
      path,
      search,
      enableSearchByNameOrNumber,
      classification,
      sortKey,
      sortDirection,
      pageIndex,
      account,
      project_type,
      created_on,
    ],
  );

  const value = useSWRAlloy(projectsUrl, fetchWithTokenAndRegion);

  return {
    ...value,
    data: value?.data || DATA_EMPTY_STATE,
  };
};
